<template>
  <v-app>
    <!-- Header -->
    <HeaderDealer />

    <div class="login-container">
      <v-row justify="center" align="center" class="fill-height">
        <v-col cols="12" sm="8" md="6" lg="4">
          <v-card class="elevation-12 login-card">
            <v-card-title class="text-center pa-6">
              <div class="w-100">
                <!-- <div class="logo-container mb-4">
                  <img src="@/assets/img/logo.png" alt="AutoSyncX™" class="logo" />
                  <div class="logo-text mt-2">
                    <span class="brand-name">AutoSyncX™</span>
                    <span class="dealer-badge">Dealer Portal</span>
                  </div>
                </div> -->
                <h2 class="login-title">Welcome Back</h2>
                <p class="login-subtitle">Sign in to your dealer dashboard</p>
              </div>
            </v-card-title>

            <v-card-text class="pa-6">
              <v-form ref="form" v-model="valid" lazy-validation>
                <v-text-field
                  v-model="email"
                  :rules="emailRules"
                  label="Email or Username"
                  type="email"
                  required
                  outlined
                  color="#3b82f6"
                  class="mb-3 custom-input"
                ></v-text-field>

                <v-text-field
                  v-model="password"
                  :rules="passwordRules"
                  label="Password"
                  :type="showPassword ? 'text' : 'password'"
                  :append-icon="showPassword ? 'material-icons-round visibility' : 'material-icons-round visibility_off'"
                  @click:append="showPassword = !showPassword"
                  required
                  outlined
                  color="#3b82f6"
                  class="mb-3 custom-input"
                ></v-text-field>

                <v-row class="mb-3">
                  <v-col cols="6">
                    <v-checkbox
                      v-model="rememberMe"
                      label="Remember me"
                      color="#3b82f6"
                      class="custom-checkbox"
                    ></v-checkbox>
                  </v-col>
                  <v-col cols="6" class="text-right">
                    <v-btn
                      text
                      class="forgot-password-btn"
                      small
                      @click="forgotPassword"
                    >
                      Forgot Password?
                    </v-btn>
                  </v-col>
                </v-row>

                <v-btn
                  :disabled="!valid || loading"
                  :loading="loading"
                  class="login-btn mr-4 mb-3"
                  large
                  block
                  @click="login"
                >
                  <span class="material-icons-round mr-2">login</span>
                  Login to Dashboard
                </v-btn>

                <v-divider class="my-4"></v-divider>

                <div class="text-center">
                  <p class="grey--text mb-2">Don't have a dealer account?</p>
                  <v-btn
                    text
                    class="signup-link-btn"
                    @click="goToSignup"
                  >
                    <span class="material-icons-round mr-2">business</span>
                    Register as Dealer
                  </v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Loading Overlay -->
    <v-overlay :value="loading">
      <v-progress-circular
        indeterminate
        size="64"
        color="primary"
      ></v-progress-circular>
    </v-overlay>

    <!-- Footer -->
    <FooterDealer />
  </v-app>
</template>

<script>
import HeaderDealer from '@/components/Layout/HeaderDealer.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'

export default {
  name: 'DealerLogin',
  components: {
    HeaderDealer,
    FooterDealer
  },
  data() {
    return {
      valid: false,
      email: '',
      password: '',
      showPassword: false,
      rememberMe: false,
      loading: false,
      emailRules: [
        v => !!v || 'Email or Username is required',
        v => /.+@.+\..+/.test(v) || v.length >= 3 || 'Enter valid email or username'
      ],
      passwordRules: [
        v => !!v || 'Password is required',
        v => (v && v.length >= 6) || 'Password must be at least 6 characters'
      ]
    }
  },
  beforeCreate() {
    // Check if already logged in as dealer
    if (localStorage.getItem('token') && localStorage.getItem('user_flag') === 'dealer') {
      this.$router.push('/dealers/dashboard')
    }
  },
  methods: {
    async login() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.loading = true

      try {
        const response = await axios.post('/api/auth/login-dealer', {
          email: this.email,
          password: this.password
        })

        const data = response.data

        if (data.flag === 1) {
          // Store authentication data
          localStorage.setItem('token', data.token.token)
          localStorage.setItem('user_id', data.data.user_id)
          localStorage.setItem('user_flag', data.data.flag)
          localStorage.setItem('user_name', data.data.first_name + ' ' + data.data.last_name)
          localStorage.setItem('user_email', data.data.email)
          localStorage.setItem('business_name', data.data.business_name)
          
          // Store dealer-specific data
          if (data.data.dealer_info) {
            localStorage.setItem('dealer_id', data.data.dealer_info.dealer_id)
            localStorage.setItem('dealer_status', data.data.dealer_info.status)
          }

          // Remember me functionality
          if (this.rememberMe) {
            localStorage.setItem('remember_dealer_email', this.email)
          } else {
            localStorage.removeItem('remember_dealer_email')
          }

          this.$toast.success('Login successful! Welcome to your dealer dashboard.', {
            position: 'top-right'
          })

          // Redirect to dealer dashboard
          this.$router.push('/dealers/dashboard')
        } else {
          this.$toast.error(data.message || 'Invalid credentials', {
            position: 'top-right'
          })
        }
      } catch (error) {
        console.error('Login error:', error)
        this.$toast.error('Login failed. Please try again.', {
          position: 'top-right'
        })
      } finally {
        this.loading = false
      }
    },

    forgotPassword() {
      // TODO: Implement forgot password functionality
      this.$toast.info('Forgot password feature coming soon. Please contact support.', {
        position: 'top-right'
      })
    },

    goToSignup() {
      this.$router.push('/dealers/signup')
    }
  },

  mounted() {
    // Auto-fill email if remembered
    const rememberedEmail = localStorage.getItem('remember_dealer_email')
    if (rememberedEmail) {
      this.email = rememberedEmail
      this.rememberMe = true
    }
  }
}
</script>

<style scoped>
.login-container {
  min-height: calc(100vh - 160px);
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  margin-top: 80px;
}

.login-card {
  border-radius: 20px !important;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  backdrop-filter: blur(10px) !important;
  background: rgba(255, 255, 255, 0.95) !important;
}

.logo-container {
  text-align: center;
}

.logo {
  max-height: 50px;
  width: auto;
}

.logo-text {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.brand-name {
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a202c;
  line-height: 1;
  font-family: 'Inter', sans-serif;
  letter-spacing: -0.02em;
}

.dealer-badge {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
  color: white;
  padding: 4px 12px;
  border-radius: 12px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
}

.login-title {
  font-size: 1.8rem !important;
  font-weight: 700 !important;
  color: #1a202c !important;
  margin-bottom: 8px !important;
  font-family: 'Inter', sans-serif !important;
}

.login-subtitle {
  color: #64748b !important;
  font-size: 1rem !important;
  font-weight: 400 !important;
  margin-bottom: 0 !important;
}

.fill-height {
  min-height: calc(100vh - 160px);
}

.w-100 {
  width: 100%;
}

/* Button Styles */
.login-btn {
  background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%) !important;
  color: white !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-radius: 12px !important;
  padding: 12px 24px !important;
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3) !important;
  transition: all 0.3s ease !important;
  border: none !important;
}

.login-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px rgba(59, 130, 246, 0.4) !important;
}

.login-btn:disabled {
  background: #e2e8f0 !important;
  color: #94a3b8 !important;
  transform: none !important;
  box-shadow: none !important;
}

.signup-link-btn {
  color: #3b82f6 !important;
  font-weight: 500 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.signup-link-btn:hover {
  background-color: rgba(59, 130, 246, 0.08) !important;
  color: #1d4ed8 !important;
}

/* Form Styles */
.custom-input >>> .v-input__control .v-input__slot {
  border-color: #e2e8f0 !important;
}

.custom-input >>> .v-input--is-focused .v-input__control .v-input__slot {
  border-color: #3b82f6 !important;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.1) !important;
}

.custom-input >>> .v-text-field__details {
  color: #64748b !important;
}

.custom-checkbox >>> .v-input--selection-controls__input {
  color: #3b82f6 !important;
}

.forgot-password-btn {
  color: #64748b !important;
  font-size: 0.875rem !important;
  font-weight: 400 !important;
  text-transform: none !important;
  padding: 4px 8px !important;
}

.forgot-password-btn:hover {
  color: #3b82f6 !important;
  background-color: rgba(59, 130, 246, 0.05) !important;
}

/* Material Icons styling */
.material-icons-round {
  font-family: 'Material Icons Round';
  font-weight: normal;
  font-style: normal;
  font-size: 24px;
  line-height: 1;
  letter-spacing: normal;
  text-transform: none;
  display: inline-block;
  white-space: nowrap;
  word-wrap: normal;
  direction: ltr;
  -webkit-font-feature-settings: 'liga';
  -webkit-font-smoothing: antialiased;
}

/* Responsive Design */
@media (max-width: 600px) {
  .login-container {
    padding: 20px 10px;
    margin-top: 80px;
  }

  .login-card {
    margin: 0 10px;
  }

  .brand-name {
    font-size: 1.3rem;
  }

  .login-title {
    font-size: 1.5rem !important;
  }
}
</style>
